import React, { useEffect, useState } from "react";

import { StatsAggregationPeriodDto } from "services/stats/restModels";
import { dateToInputValue, endOfDay, inputValueToDate, startOfDay } from "shared/lib/dateInput";
import {
    estimateChartPoints,
    MAX_CHART_POINTS,
    STATS_INTERVAL_PERIOD_OPTIONS,
    STATS_PERIOD_OPTIONS,
} from "shared/lib/statsPeriods";
import {
    Button,
    ChipToggle,
    DateField,
    LabeledField,
    Modal,
    Select,
    TabItem,
    Tabs,
    TextInput,
} from "shared/ui";

import { useStatisticsContext } from "features/Statistics/context";

import { CustomFields, Detailing, Periods } from "./styles";

type PeriodTab = "presets" | "custom";

const PERIOD_TABS: TabItem<PeriodTab>[] = [
    { key: "presets", label: "Периоды" },
    { key: "custom", label: "Произвольно" },
];

interface PeriodPickerModalProps {
    open: boolean;
    onClose: () => void;
}

export const PeriodPickerModal = ({ open, onClose }: PeriodPickerModalProps) => {
    const { period, range, setPeriod, setRange, interval, setInterval } = useStatisticsContext();

    const [tab, setTab] = useState<PeriodTab>(period === "custom" ? "custom" : "presets");

    /*
    * У поля шага свой стейт: оно должно уметь быть пустым, пока стирают последнюю цифру.
    * Полностью контролируемое от `interval.amount`, оно возвращало бы старое число на backspace.
    * */
    const [amountInput, setAmountInput] = useState<string>(String(interval.amount));

    // Шаг меняется и снаружи — смена пресета возвращает его к собственному шагу периода.
    useEffect(() => setAmountInput(String(interval.amount)), [interval.amount]);

    const changeAmount = (value: string) => {
        setAmountInput(value);

        const amount = Number(value);

        if (value.trim() === "" || isNaN(amount) || amount < 1) return;

        setInterval({ amount: Math.floor(amount), period: interval.period });
    };

    const isTooDetailed = estimateChartPoints(range, interval) > MAX_CHART_POINTS;

    const changeCustomBound = (bound: "from" | "to", value: string) => {
        const parsed = inputValueToDate(value);

        // Недописанную дату не применяем: пока набирают год, поле отдаёт «0002-01-01», и
        // период уезжал бы в нулевой год прямо во время ввода.
        if (!parsed) return;

        setRange(
            bound === "from"
                ? { from: startOfDay(parsed), to: range.to }
                : { from: range.from, to: endOfDay(parsed) }
        );
    };

    return (
        <Modal
            open={open}
            onClose={onClose}
            title="Период"
            actions={<Button onClick={onClose}>Готово</Button>}
        >
            <Tabs<PeriodTab> value={tab} tabs={PERIOD_TABS} onChange={setTab} />

            {tab === "presets" ? (
                <Periods>
                    {STATS_PERIOD_OPTIONS.map((option) => (
                        <ChipToggle
                            key={option.key}
                            active={option.key === period}
                            size="small"
                            onClick={() => setPeriod(option.key)}
                        >
                            {option.label}
                        </ChipToggle>
                    ))}
                </Periods>
            ) : (
                <CustomFields>
                    <LabeledField label="С" htmlFor="stats-period-from">
                        <DateField
                            id="stats-period-from"
                            value={dateToInputValue(range.from)}
                            max={dateToInputValue(range.to)}
                            onChange={(value) => changeCustomBound("from", value)}
                        />
                    </LabeledField>
                    <LabeledField label="По" htmlFor="stats-period-to">
                        <DateField
                            id="stats-period-to"
                            value={dateToInputValue(range.to)}
                            min={dateToInputValue(range.from)}
                            onChange={(value) => changeCustomBound("to", value)}
                        />
                    </LabeledField>
                </CustomFields>
            )}

            <Detailing>
                <LabeledField label="Шаг" htmlFor="stats-interval-amount">
                    <TextInput
                        id="stats-interval-amount"
                        type="number"
                        value={amountInput}
                        onChange={changeAmount}
                    />
                </LabeledField>
                <LabeledField
                    label="Детализация графика"
                    hint={
                        isTooDetailed
                            ? "Слишком мелкий шаг для выбранного периода — график построить не получится"
                            : "Шаг точки на графике. По умолчанию подбирается под длину периода"
                    }
                    hintTone={isTooDetailed ? "danger" : "default"}
                >
                    <Select
                        value={interval.period}
                        options={STATS_INTERVAL_PERIOD_OPTIONS.map((option) => ({
                            value: option.value,
                            label: option.label,
                        }))}
                        onChange={(value) =>
                            setInterval({
                                amount: interval.amount,
                                period: value as StatsAggregationPeriodDto,
                            })
                        }
                    />
                </LabeledField>
            </Detailing>
        </Modal>
    );
};
